// /app/frontend/src/components/ThemeToggle.jsx
/**
 * ThemeToggle
 * ===========
 * Quick-access light/dark theme toggle button.
 * Placed in GlobalNav for easy access.
 */

import React, { useState, useEffect } from 'react';
import { Sun, Moon } from 'lucide-react';

// Standalone theme hook that doesn't require context
function useLocalTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('unitylove_theme');
      if (saved === 'dark' || saved === 'light') return saved;
      if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
    localStorage.setItem('unitylove_theme', theme);
  }, [theme]);

  // 🎯 PATCH: Listen for theme changes from other ThemeToggle instances
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.theme;
      if (t === 'dark' || t === 'light') {
        setThemeState(t);
        if (t === 'dark') document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
      }
    };
    window.addEventListener('unitylove_theme_change', handler);
    return () => window.removeEventListener('unitylove_theme_change', handler);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(nextTheme);
    // 🎯 PATCH: Broadcast theme change to sync all instances
    window.dispatchEvent(new CustomEvent('unitylove_theme_change', { detail: { theme: nextTheme } }));
  };

  return { theme, toggleTheme, isDark: theme === 'dark' };
}

export default function ThemeToggle({ className = '' }) {
  const { theme, toggleTheme, isDark } = useLocalTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`p-2 rounded-lg transition-colors ${
        isDark 
          ? 'bg-slate-800 hover:bg-slate-700 text-amber-400' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
      } ${className}`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {isDark ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </button>
  );
}

export function ThemeToggleCompact({ className = '' }) {
  const { theme, toggleTheme, isDark } = useLocalTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
        isDark 
          ? 'bg-slate-800 hover:bg-slate-700 text-amber-400' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
      } ${className}`}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {isDark ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </button>
  );
}
